import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import type { DropdownOption } from '~/components/Dropdown';
import { useIsExternalUser } from '~/hooks/useIsExternalUser';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';
import { useLabelGroupList } from '~/modules/settings/api/queries/labelGroupList';
import { useLabelList } from '~/modules/settings/api/queries/labelList';
import {
  EXTERNAL_ROLES,
  INTERNAL_ROLES,
  LABEL_TYPES,
  USER_STATUS,
  USER_TYPES,
} from '~/modules/settings/constants/Filter';
import type { PaginatedResponse } from '~settings/api/lib/schemas';

export interface Filter {
  filterTitle: string;
  label: string;
  value: string;
  queryParam: string;
}

interface DynamicFilterItem {
  id: string;
  label: string;
}

const getDynamicFilters = (
  values: string[],
  entityMap?: Record<string, string>,
): DynamicFilterItem[] => {
  if (!entityMap) {
    return [];
  }

  return values.reduce<DynamicFilterItem[]>((acc, next) => {
    if (entityMap[next]) {
      acc.push({ id: next, label: entityMap[next] });
    }

    return acc;
  }, []);
};

const getBooleanFilter = (
  values: string[],
  entityArr: DropdownOption[],
  t: (key: string) => string,
): DynamicFilterItem[] => {
  const value = entityArr.find((item) => item.value === values[0])?.label || '';

  return [{ id: values[0], label: t(value) }];
};

const selectFilters = <TData extends { id: string; name: string }>(
  data: PaginatedResponse<TData>,
): Record<string, string> =>
  data.data.reduce<Record<string, string>>((acc, next) => {
    acc[next.id] = next.name;

    return acc;
  }, {});

export const useAppliedFilters = () => {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('settings');

  const [labelGroupsEnabled] = useFeatureFlags([
    FEATURE_FLAGS.ContainerSettingsLabelGroups,
  ]);
  const isCurrentUserInternal = !useIsExternalUser();

  const shouldLoadLabelGroups =
    labelGroupsEnabled &&
    isCurrentUserInternal &&
    searchParams.has('labelGroupIds');

  const labelIds = searchParams.get('labelIds');
  const labelGroupIds = searchParams.get('labelGroupIds');
  const parentLabelsIds = searchParams.get('parentIds');

  const { labelList, isLoading: isLabelsLoading } = useLabelList(
    { labelIds: labelIds ?? '' },
    {
      select: selectFilters,
      enabled: !!labelIds,
    },
  );

  const { labelList: parentLabelsList, isLoading: isParentLabelLoading } =
    useLabelList(
      { labelIds: parentLabelsIds ?? '' },
      {
        select: selectFilters,
        enabled: !!parentLabelsIds,
      },
    );

  const { labelGroupList, isLoading: isLabelGroupsLoading } = useLabelGroupList(
    { labelGroupIds: labelGroupIds ?? '' },
    {
      select: selectFilters,
      enabled: shouldLoadLabelGroups,
    },
  );

  const isLoading =
    isLabelsLoading || isLabelGroupsLoading || isParentLabelLoading;

  const filtersMap = useMemo(
    () => ({
      isActive: {
        title: t('status'),
        getValues: (values: string[]) =>
          getBooleanFilter(values, USER_STATUS, t),
      },
      roles: {
        title: t('role'),
        getValues: (values: string[]) => {
          return values.map((value) => {
            const role = [...INTERNAL_ROLES, ...EXTERNAL_ROLES].find(
              (item) => item.value === value,
            );

            return { id: role!.value, label: t(role!.label) };
          });
        },
      },
      isInternal: {
        title: t('userType'),
        getValues: (values: string[]) =>
          getBooleanFilter(values, USER_TYPES, t),
      },
      labelType: {
        title: t('labelType'),
        getValues: (values: string[]) =>
          getBooleanFilter(values, LABEL_TYPES, t),
      },
      labelIds: {
        title: t('label.title'),
        getValues: (values: string[]) => getDynamicFilters(values, labelList),
      },
      labelGroupIds: {
        title: t('labelGroup.title'),
        getValues: (values: string[]) =>
          getDynamicFilters(values, labelGroupList),
      },
      parentIds: {
        title: t('parentLabel'),
        getValues: (values: string[]) =>
          getDynamicFilters(values, parentLabelsList),
      },
    }),
    [labelGroupList, labelList, parentLabelsList, t],
  );

  const filters = useMemo(
    () =>
      [...searchParams.entries()].reduce<Filter[]>((acc, [key]) => {
        const filterConfig = filtersMap[key as keyof typeof filtersMap];

        if (filterConfig) {
          const { title, getValues } = filterConfig;
          const ids: string[] =
            searchParams.get(key)?.split(',').filter(Boolean) ?? [];
          const values = getValues(ids);

          values?.forEach(({ id, label }) => {
            acc.push({
              value: id,
              filterTitle: title,
              label,
              queryParam: key,
            });
          });
        }

        return acc;
      }, []),
    [filtersMap, searchParams],
  );

  return {
    filters,
    isLoading,
  };
};
